import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { can } from '../permissions';
import { GitCompare, CheckCircle2, XCircle, Clock, AlertTriangle, FileStack, Download, Search, RotateCcw, ChevronDown } from 'lucide-react';

const C = { blue: '#0284c7', green: '#16a34a', red: '#dc2626', amber: '#f59e0b', purple: '#7c3aed', slate: '#64748b' };
const money = n => 'AED ' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const num = n => Number(n || 0).toLocaleString();
// Local calendar date, not toISOString() — that converts to UTC first, which
// silently rolls the date back a day for anyone east of UTC (e.g. UAE).
const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const niceDate = d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const card = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
const th = { padding: '10px 14px', textAlign: 'left', color: '#64748b', fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' };
const td = { padding: '10px 14px', color: '#475569', fontSize: 13 };
const yesNo = v => v ? 'Yes' : 'No';
// Business Central doesn't expose Closed/Corrective/Cancelled as their own
// fields on the standard salesInvoices API — they're derived from fields
// that ARE already there: status carries "Corrective" and "Canceled" as
// literal values, and a posted invoice counts as Closed once nothing is
// left to pay (remainingAmount is 0).
const isClosed = inv => Number(inv.remainingAmount || 0) <= 0;
const isCorrective = inv => (inv.status || '').toLowerCase() === 'corrective';
const isCancelled = inv => (inv.status || '').toLowerCase().startsWith('cancel');

// LogisticsOp's own delivery status for this invoice (as opposed to the
// Cancelled column above, which is Business Central's own status field).
// Same 5 states, same colors, as the Live Map's delivery pins.
const STATUS_META = {
  delivered: { label: 'Delivered', color: C.green },
  cancelled: { label: 'Cancelled', color: C.red },
  rescheduled: { label: 'Rescheduled', color: C.purple },
  in_plan: { label: 'Pending / In Plan', color: C.blue },
  not_scheduled: { label: 'Not Scheduled', color: C.amber },
};
const statusMeta = key => STATUS_META[key] || { label: key || '—', color: C.slate };

const PRESETS = [
  { key: 'month', label: 'This month', range: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: iso(from), to: iso(now) };
    } },
  { key: 'lastmonth', label: 'Last month', range: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const to = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: iso(from), to: iso(to) };
    } },
  { key: 'week', label: 'This week', range: () => {
      const to = new Date(); const from = new Date(); from.setDate(from.getDate() - 6);
      return { from: iso(from), to: iso(to) };
    } },
];

const StatTile = ({ icon: Icon, label, value, sub, color }) => (
  <div style={{ ...card, padding: 18, display: 'flex', alignItems: 'flex-start', gap: 14, flex: 1, minWidth: 190 }}>
    <div style={{ background: color + '20', borderRadius: 10, padding: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={20} color={color} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', lineHeight: 1.15 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{sub}</div>}
    </div>
  </div>
);

// A dropdown of checkboxes rather than a native <select multiple> — easier
// to scan, and shows invoice counts per salesperson the way the old
// single-select did. Closes itself on an outside click.
function SalespeopleMultiSelect({ options, selected, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggle = sp => {
    onChange(selected.includes(sp) ? selected.filter(s => s !== sp) : [...selected, sp]);
  };

  const label = loading ? 'Loading salespeople…'
    : selected.length === 0 ? 'All Salespeople'
    : selected.length === 1 ? selected[0]
    : `${selected.length} salespeople selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} disabled={loading} style={{
        padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, color: '#334155',
        background: '#fff', cursor: loading ? 'default' : 'pointer', minWidth: 190, textAlign: 'left',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <ChevronDown size={14} color="#94a3b8" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && !loading && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 20, background: '#fff',
          border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          minWidth: 230, maxHeight: 300, overflowY: 'auto', padding: 6 }}>
          <div style={{ display: 'flex', gap: 8, padding: '4px 6px 8px', borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}>
            <button type="button" onClick={() => onChange(options.map(o => o.salesperson))} style={{
              fontSize: 11, color: '#0284c7', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
              Select all
            </button>
            <span style={{ color: '#e2e8f0' }}>|</span>
            <button type="button" onClick={() => onChange([])} style={{
              fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
              Clear
            </button>
          </div>
          {options.map(sp => (
            <label key={sp.salesperson} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
              borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#334155' }}>
              <input type="checkbox" checked={selected.includes(sp.salesperson)} onChange={() => toggle(sp.salesperson)} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sp.salesperson}</span>
              <span style={{ color: '#94a3b8', fontSize: 11 }}>{sp.invoices}</span>
            </label>
          ))}
          {options.length === 0 && (
            <div style={{ padding: 8, fontSize: 12, color: '#94a3b8' }}>No salespeople for this range</div>
          )}
        </div>
      )}
    </div>
  );
}

// The 9 columns originally requested, same set Business Central's own Posted
// Sales Invoices list shows, plus Portal Status (LogisticsOp's own delivery
// status) tacked on at the end — used for both the on-screen table and the
// Excel/CSV download, so the two never drift apart.
const COLUMNS = [
  { key: 'postingDate', label: 'Posting Date', get: inv => inv.postingDate },
  { key: 'number', label: 'No.', get: inv => inv.number },
  { key: 'customerNumber', label: 'Customer No.', get: inv => inv.customerNumber },
  { key: 'customerName', label: 'Customer Name', get: inv => inv.customerName },
  { key: 'salesperson', label: 'Salesperson Code', get: inv => (inv.salesperson || '').trim() },
  { key: 'amount', label: 'Amount Including VAT', get: inv => Number(inv.totalAmountIncludingTax || 0), money: true },
  { key: 'closed', label: 'Closed', get: inv => yesNo(isClosed(inv)) },
  { key: 'corrective', label: 'Corrective', get: inv => yesNo(isCorrective(inv)) },
  { key: 'cancelled', label: 'Cancelled', get: inv => yesNo(isCancelled(inv)) },
  { key: 'portalStatus', label: 'Portal Status', get: inv => statusMeta(inv._status).label, badge: true },
];

const InvoiceRow = ({ inv }) => (
  <tr style={{ borderTop: '1px solid #f8fafc' }}>
    {COLUMNS.map(col => {
      if (col.badge) {
        const meta = statusMeta(inv._status);
        return (
          <td key={col.key} style={td}>
            <span style={{
              display: 'inline-block', padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: meta.color + '18', color: meta.color }}>{meta.label}</span>
          </td>
        );
      }
      return (
        <td key={col.key} style={{ ...td, ...(col.money ? { textAlign: 'right', fontWeight: 600 } : {}), ...(col.key === 'number' ? { fontWeight: 600, color: '#1e293b' } : {}) }}>
          {col.money ? money(col.get(inv)) : (col.get(inv) || (col.key === 'salesperson' ? '(none)' : '—'))}
        </td>
      );
    })}
  </tr>
);

function toCsv(rows) {
  if (!rows.length) return '';
  const esc = v => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const lines = [COLUMNS.map(c => esc(c.label)).join(',')];
  rows.forEach(r => lines.push(COLUMNS.map(c => esc(c.get(r))).join(',')));
  return lines.join('\n');
}

const sameSet = (a, b) => a.length === b.length && [...a].sort().join('\u0001') === [...b].sort().join('\u0001');

// Business Central posted invoices for a date range, matched against
// LogisticsOp's own delivery status by invoice number — same "already
// delivered" / "already in a plan" rule routes/bc.js already uses for
// import, so these numbers stay consistent with what /bc/import would
// actually do with these same invoices. The salesperson list for the chosen
// date range loads on its own, lightweight call (/bc/salespeople) so it's
// ready to pick from before any invoices are pulled — the actual invoice
// pull (the expensive one, filtered to whichever salespeople are selected)
// only happens when "Pull from Business Central" is clicked, never
// automatically. routes/bc.js now pages through Business Central's full
// result set, so there is no cap on how many invoices a pull can bring back.
export default function BcReconciliation() {
  const [presetKey, setPresetKey] = useState('month');
  const [customFrom, setCustomFrom] = useState(PRESETS[0].range().from);
  const [customTo, setCustomTo] = useState(PRESETS[0].range().to);
  const presetRange = useMemo(() => PRESETS.find(p => p.key === presetKey)?.range() || { from: '', to: '' }, [presetKey]);
  const from = presetKey === 'custom' ? customFrom : presetRange.from;
  const to = presetKey === 'custom' ? customTo : presetRange.to;

  const [selSalespeople, setSelSalespeople] = useState([]);
  const [salespeopleOptions, setSalespeopleOptions] = useState([]);
  const [spLoading, setSpLoading] = useState(false);
  const [spError, setSpError] = useState('');
  const [search, setSearch] = useState('');

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pulledFor, setPulledFor] = useState(null); // { from, to, salespeople } of the last successful pull

  // Populate the salesperson dropdown for whichever date range is chosen —
  // this is just a list of names (with counts), not the reconciliation
  // itself, so it's fine to load it as soon as the date range settles,
  // ahead of the operator picking salespeople and clicking Pull.
  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setSpLoading(true);
    setSpError('');
    api.get(`/api/bc/salespeople?from=${from}&to=${to}`)
      .then(r => {
        if (cancelled) return;
        setSalespeopleOptions(r.data.salespeople || []);
      })
      .catch(e => {
        if (cancelled) return;
        setSalespeopleOptions([]);
        setSpError(e.response?.data?.error || 'Could not load salespeople for this range');
      })
      .finally(() => { if (!cancelled) setSpLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);

  const pull = () => {
    if (!from || !to) return;
    setLoading(true);
    setError('');
    const q = selSalespeople.length ? `&salespeople=${encodeURIComponent(selSalespeople.join(','))}` : '';
    api.get(`/api/bc/reconciliation?from=${from}&to=${to}${q}`)
      .then(r => {
        setData(r.data);
        setPulledFor({ from, to, salespeople: selSalespeople });
      })
      .catch(e => { setData(null); setError(e.response?.data?.error || 'Could not load Business Central data'); })
      .finally(() => setLoading(false));
  };

  const filteredInvoices = useMemo(() => {
    const rows = data?.invoices || [];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(i =>
      String(i.number || '').toLowerCase().includes(q) ||
      String(i.customerName || '').toLowerCase().includes(q) ||
      String(i.customerNumber || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const downloadCsv = () => {
    const csv = toCsv(data?.invoices || []);
    if (!csv) return;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    // .csv, not .xls — a CSV opens straight into Excel by default on
    // double-click, whereas a .xls extension on CSV content makes Excel
    // show a "format doesn't match extension" warning first.
    a.href = url; a.download = `bc-reconciliation_${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!can('bc_reconciliation', 'view')) {
    return <div style={{ padding: 24 }}>You don't have access to this page.</div>;
  }

  const covered = data && data.total_posted > 0
    ? Math.round(((data.total_posted - data.not_scheduled.count) / data.total_posted) * 100)
    : null;
  const stale = data && pulledFor && (pulledFor.from !== from || pulledFor.to !== to || !sameSet(pulledFor.salespeople, selSalespeople));

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>BC Reconciliation</h1>
          <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
            Business Central posted invoices vs. delivery status
            {pulledFor && ` · last pulled ${niceDate(pulledFor.from)} – ${niceDate(pulledFor.to)}${pulledFor.salespeople.length ? ` · ${pulledFor.salespeople.join(', ')}` : ''}`}
          </p>
        </div>
      </div>

      <div style={{ ...card, padding: 16, marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {PRESETS.map(p => (
          <button key={p.key} onClick={() => setPresetKey(p.key)} style={{
            padding: '8px 16px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
            background: presetKey === p.key ? '#1e293b' : '#f8fafc', color: presetKey === p.key ? '#fff' : '#64748b' }}>{p.label}</button>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f8fafc', borderRadius: 8, padding: '6px 10px' }}>
          <input type="date" value={customFrom} onChange={e => { setCustomFrom(e.target.value); setPresetKey('custom'); }} style={{ border: 'none', background: 'transparent', fontSize: 12, color: '#1e293b', outline: 'none', fontFamily: 'inherit' }} />
          <span style={{ color: '#94a3b8', fontSize: 12 }}>–</span>
          <input type="date" value={customTo} onChange={e => { setCustomTo(e.target.value); setPresetKey('custom'); }} style={{ border: 'none', background: 'transparent', fontSize: 12, color: '#1e293b', outline: 'none', fontFamily: 'inherit' }} />
        </div>
        <SalespeopleMultiSelect options={salespeopleOptions} selected={selSalespeople} onChange={setSelSalespeople} loading={spLoading} />
        <button onClick={pull} disabled={loading || !from || !to} style={{
          padding: '8px 18px', border: 'none', borderRadius: 8, cursor: loading ? 'default' : 'pointer', fontSize: 13, fontWeight: 700,
          background: C.purple, color: '#fff', opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
          <GitCompare size={14} /> {loading ? 'Pulling…' : 'Pull from Business Central'}
        </button>
        {stale && !loading && (
          <span style={{ fontSize: 12, color: '#b45309' }}>Filters changed — click Pull to refresh</span>
        )}
        {spError && !spLoading && (
          <span style={{ fontSize: 12, color: '#dc2626' }}>{spError}</span>
        )}
      </div>

      {loading && <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Pulling from Business Central…</div>}

      {!loading && error && (
        <div style={{ ...card, padding: 20, color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{error}</div>
      )}

      {!loading && !error && !data && (
        <div style={{ ...card, padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
          Choose a date range and, if you want, one or more salespeople above, then click <strong>Pull from Business Central</strong>.
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
            <StatTile icon={FileStack} label="Posted in Business Central" value={num(data.total_posted)} sub={money(data.total_value)} color={C.purple} />
            <StatTile icon={CheckCircle2} label="Delivered" value={num(data.delivered.count)} sub={money(data.delivered.value)} color={C.green} />
            <StatTile icon={XCircle} label="Cancelled" value={num(data.cancelled.count)} sub={money(data.cancelled.value)} color={C.red} />
            <StatTile icon={RotateCcw} label="Rescheduled" value={num(data.rescheduled.count)} sub={money(data.rescheduled.value)} color={C.purple} />
            <StatTile icon={Clock} label="Pending / In Plan" value={num(data.in_plan.count)} sub={money(data.in_plan.value)} color={C.blue} />
            <StatTile icon={AlertTriangle} label="Not Scheduled" value={num(data.not_scheduled.count)} sub={money(data.not_scheduled.value)} color={C.amber} />
          </div>

          {covered !== null && (
            <div style={{ ...card, padding: '12px 20px', marginBottom: 16, fontSize: 13, color: '#475569' }}>
              <strong style={{ color: '#1e293b' }}>{covered}%</strong> of invoices posted in Business Central this pull have made it into LogisticsOp (delivered, cancelled, rescheduled, or on a route). The rest — <strong style={{ color: '#b45309' }}>{num(data.not_scheduled.count)}</strong> — were never imported.
            </div>
          )}

          {!selSalespeople.length && data.by_salesperson && data.by_salesperson.length > 1 && (
            <div style={{ ...card, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
                <GitCompare size={15} color="#64748b" />
                <h2 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: 0 }}>By Salesperson</h2>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <th style={th}>Salesperson</th>
                      <th style={{ ...th, textAlign: 'right' }}>Posted</th>
                      <th style={{ ...th, textAlign: 'right', color: C.green }}>Delivered</th>
                      <th style={{ ...th, textAlign: 'right', color: C.red }}>Cancelled</th>
                      <th style={{ ...th, textAlign: 'right', color: C.purple }}>Rescheduled</th>
                      <th style={{ ...th, textAlign: 'right', color: C.blue }}>In Plan</th>
                      <th style={{ ...th, textAlign: 'right', color: C.amber }}>Not Scheduled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_salesperson.map((sp, i) => (
                      <tr key={sp.salesperson} style={{ borderTop: i ? '1px solid #f8fafc' : 'none', cursor: 'pointer' }}
                        onClick={() => setSelSalespeople([sp.salesperson])}>
                        <td style={{ ...td, fontWeight: 600, color: '#1e293b' }}>{sp.salesperson}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{num(sp.total)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{num(sp.delivered)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{num(sp.cancelled)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{num(sp.rescheduled)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{num(sp.in_plan)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{num(sp.not_scheduled)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ ...card, overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileStack size={15} color="#64748b" />
                <h2 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: 0 }}>Invoices ({num(filteredInvoices.length)}{search ? ` of ${num(data.invoices.length)}` : ''})</h2>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f8fafc', borderRadius: 8, padding: '6px 10px' }}>
                  <Search size={13} color="#94a3b8" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search invoice, customer…"
                    style={{ border: 'none', background: 'transparent', fontSize: 12, color: '#1e293b', outline: 'none', width: 160 }} />
                </div>
                <button onClick={downloadCsv} style={{
                  padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: '#fff', color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Download size={13} /> Export to Excel
                </button>
              </div>
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #f1f5f9', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                    {COLUMNS.map(col => (
                      <th key={col.key} style={col.money ? { ...th, textAlign: 'right' } : th}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map(inv => (
                    <InvoiceRow key={inv.number} inv={inv} />
                  ))}
                </tbody>
              </table>
              {filteredInvoices.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No invoices match that search.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
